import { Skeleton } from 'antd'
import { type FC, useId } from 'react'

import { MAX_NAME, MAX_NOTE, SERIES_TEXT } from 'features/Series/series.constants'

import type { PersonOption } from '../../ProviderField'
import controlStyles from '../controls/controls.module.scss'
import { SeriesCheckbox } from '../controls/SeriesCheckbox'
import { SeriesGrid } from '../controls/SeriesGrid'
import { SeriesStepper } from '../controls/SeriesStepper'
import { UnitSegmented } from '../controls/UnitSegmented'
import { toUsDate } from '../seriesDates'
import { optionsWithRun } from '../seriesPeople'
import { SERIES_TEXT_V2 } from '../seriesText'
import styles from '../series.module.scss'
import editStyles from './seriesEdit.module.scss'
import type { SeriesEdit } from './useSeriesEdit'

type Problem = { id: number; title: string }

type SeriesEditPanelProps = {
  edit: SeriesEdit
  doctorOptions: PersonOption[]
  openProblems: Problem[]
}

// Open problems first, then the problems the run carries that are no longer open.
const mergeProblems = (open: Problem[], carried: Problem[]): Problem[] => [
  ...open,
  ...carried.filter((problem) => !open.some((item) => item.id === problem.id)),
]

const unitText = (unit: string, n: number): string => `${unit}${n === 1 ? '' : 's'}`

const Status: FC<{ edit: SeriesEdit }> = ({ edit }) => (
  <div className={styles.panel}>
    {edit.status === 'error' ? (
      <p className={editStyles.status}>
        {edit.isAccessDenied ? SERIES_TEXT.emrAccessDenied : SERIES_TEXT_V2.loadFailed}
        {!edit.isAccessDenied && (
          <button type='button' className={editStyles.retry} onClick={edit.retry}>
            {SERIES_TEXT_V2.tryAgain}
          </button>
        )}
      </p>
    ) : (
      <div className={editStyles.status}>
        <Skeleton active title={false} paragraph={{ rows: 3 }} />
      </div>
    )}
  </div>
)

export const SeriesEditPanel: FC<SeriesEditPanelProps> = ({
  edit,
  doctorOptions,
  openProblems,
}) => {
  const ids = useId()
  const { run, draft } = edit
  if (edit.status !== 'ready' || !run || !draft) return <Status edit={edit} />

  const { openedStep } = edit
  // Nothing changes while the save runs: its answer rebuilds the draft.
  const busy = edit.isSaving
  const gridRows = busy ? edit.rows.map((row) => ({ ...row, disabled: true })) : edit.rows
  const problems = mergeProblems(openProblems, run.issues)
  const hasIssues = draft.issueIds.length > 0
  const { n: shiftN, unit: shiftUnit } = draft.shift
  const toggleIssue = (id: number) => {
    const issueIds = draft.issueIds.includes(id)
      ? draft.issueIds.filter((item) => item !== id)
      : [...draft.issueIds, id]
    // Apply with no problems would strip the remaining steps, so the flag goes with the last chip.
    edit.editDraft(issueIds.length ? { issueIds } : { issueIds, applyIssuesToRemaining: false })
  }
  // The run's own providers stay pickable even when the site list no longer has them.
  const gridOptions = optionsWithRun(doctorOptions, run.visits)

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <div className={editStyles.pillRow}>
          <span className={editStyles.pill}>{SERIES_TEXT_V2.pill(openedStep, run.total)}</span>
          <span className={editStyles.sub}>{SERIES_TEXT_V2.pillSub(openedStep, run.total)}</span>
        </div>
        <div className={editStyles.fieldLabel}>
          <label className={styles.label} htmlFor={`${ids}-label`}>
            {SERIES_TEXT_V2.labelLabel} <span className={editStyles.required}>*</span>
          </label>
          <input
            id={`${ids}-label`}
            className={styles.input}
            type='text'
            maxLength={MAX_NAME}
            disabled={busy}
            value={draft.name}
            onChange={(event) => edit.editDraft({ name: event.target.value })}
          />
        </div>
        <div className={editStyles.fieldStarted}>
          <label className={styles.label} htmlFor={`${ids}-started`}>
            {SERIES_TEXT_V2.startedLabel}
          </label>
          <input
            id={`${ids}-started`}
            className={`${styles.input} ${editStyles.disabledBox}`}
            type='text'
            disabled
            value={run.startedOn ? toUsDate(run.startedOn) : SERIES_TEXT_V2.dash}
          />
        </div>
        <div className={editStyles.fieldNote}>
          <label className={styles.label} htmlFor={`${ids}-note`}>
            {SERIES_TEXT_V2.editNoteLabel}
          </label>
          <input
            id={`${ids}-note`}
            className={styles.input}
            type='text'
            placeholder={SERIES_TEXT_V2.notePlaceholder}
            disabled={busy}
            maxLength={MAX_NOTE}
            value={draft.note}
            onChange={(event) => edit.editDraft({ note: event.target.value })}
          />
        </div>
      </div>

      <div className={editStyles.problems}>
        <span className={editStyles.problemsLabel}>{SERIES_TEXT_V2.forProblems}</span>
        {problems.map((problem) => {
          const on = draft.issueIds.includes(problem.id)
          return (
            <button
              key={problem.id}
              type='button'
              aria-pressed={on}
              disabled={busy}
              className={on ? `${editStyles.chip} ${editStyles.chipOn}` : editStyles.chip}
              onClick={() => toggleIssue(problem.id)}>
              {on ? '✓' : '+'} {problem.title}
            </button>
          )
        })}
        <span className={editStyles.apply}>
          <SeriesCheckbox
            checked={draft.applyIssuesToRemaining && hasIssues}
            disabled={busy || !hasIssues}
            onChange={(applyIssuesToRemaining) => edit.editDraft({ applyIssuesToRemaining })}>
            {SERIES_TEXT_V2.applyRemaining}
          </SeriesCheckbox>
        </span>
        <span className={editStyles.problemsHelp}>{SERIES_TEXT_V2.applyHelp}</span>
      </div>

      <div className={styles.schedule}>
        <span className={styles.scheduleLabel}>{SERIES_TEXT_V2.scheduleLabel}</span>
        <SeriesGrid
          mode='edit'
          rows={gridRows}
          doctorOptions={gridOptions}
          onEdit={(key, patch) => edit.editVisit(Number(key), patch)}
        />
        <span className={styles.help}>{SERIES_TEXT_V2.helpEdit}</span>
      </div>

      {openedStep !== null && (
        <div className={editStyles.shift}>
          <span>{SERIES_TEXT_V2.shiftPrefix}</span>
          <UnitSegmented
            value={shiftUnit}
            ariaLabel={SERIES_TEXT_V2.shiftPrefix}
            disabled={busy}
            onChange={edit.setShiftUnit}
          />
          <button
            type='button'
            className={controlStyles.sq}
            aria-label={SERIES_TEXT_V2.shiftEarlier}
            disabled={busy}
            onClick={() => edit.shift(-1)}>
            −
          </button>
          <button
            type='button'
            className={controlStyles.sq}
            aria-label={SERIES_TEXT_V2.shiftLater}
            disabled={busy}
            onClick={() => edit.shift(1)}>
            +
          </button>
          {shiftN !== 0 && (
            <span className={editStyles.shiftNote}>
              {SERIES_TEXT_V2.shiftNote(
                openedStep,
                run.total,
                shiftN > 0 ? '+' : '−',
                Math.abs(shiftN),
                unitText(shiftUnit, Math.abs(shiftN)),
              )}
            </span>
          )}
        </div>
      )}

      <div className={editStyles.band}>
        <span>{SERIES_TEXT_V2.addPrefix}</span>
        <SeriesStepper
          value={draft.extendBy}
          min={0}
          max={edit.maxExtend}
          decLabel={SERIES_TEXT_V2.fewerExtra}
          incLabel={SERIES_TEXT_V2.moreExtra}
          disabled={busy}
          onChange={(extendBy) => edit.editDraft({ extendBy })}
        />
        <span>{SERIES_TEXT_V2.addSuffix}</span>
        <span className={editStyles.spacer} />
        <SeriesCheckbox
          weight={500}
          checked={draft.cancelled}
          disabled={busy}
          onChange={(cancelled) => edit.editDraft({ cancelled })}>
          {SERIES_TEXT_V2.cancelSeries}{' '}
          <span className={editStyles.cancelNote}>{SERIES_TEXT_V2.cancelNote}</span>
        </SeriesCheckbox>
      </div>
    </div>
  )
}
